import api from './api';

export interface ContentSection {
  type: 'text' | 'list' | 'card' | 'alert' | 'code' | 'image' | 'video';
  title?: string;
  content: string[];
  style?: {
    color?: string;
    bgcolor?: string;
    icon?: string;
    severity?: 'info' | 'success' | 'warning' | 'error';
  };
  metadata?: any;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number | number[] | string; // Single-select (number), multiple-select (array), or comma-separated string
  explanation?: string;
  points?: number;
}

export interface TrainingLesson {
  _id: string;
  moduleId: string;
  moduleName: string;
  lessonNumber: number;
  title: string;
  slug: string;
  duration: string;
  description?: string;
  contentSections: ContentSection[];
  quiz: QuizQuestion[];
  keyTakeaways: string[];
  resources?: Array<{
    title: string;
    url: string;
    type: string;
  }>;
  order: number;
  status: 'draft' | 'review' | 'published' | 'archived';
  tags?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  estimatedMinutes?: number;
}

export interface LessonProgress {
  lessonId: string;
  completed: boolean;
  completedAt?: Date;
  quizScore?: number;
  quizAttempts: number;
  timeSpent: number;
  lastAccessedAt: Date;
}

export interface TrainingProgress {
  _id: string;
  userId: string;
  moduleId: string;
  moduleName: string;
  status: 'not-started' | 'in-progress' | 'completed';
  lessons: LessonProgress[];
  finalQuizScore?: number;
  finalQuizAttempts: number;
  finalQuizPassed: boolean;
  passingScore: number;
  certificateIssued: boolean;
  certificateId?: string;
  certificateIssuedAt?: Date;
  certificateName?: string;
  startedAt?: Date;
  completedAt?: Date;
  lastAccessedAt: Date;
  totalTimeSpent: number;
  completionPercentage: number;
}

const trainingService = {
  // Get all lessons for a module
  getModuleLessons: async (moduleId: string): Promise<TrainingLesson[]> => {
    const response = await api.get(`/training/modules/${moduleId}/lessons`);
    return response.data.data;
  },

  // Get single lesson by ID
  getLesson: async (lessonId: string): Promise<TrainingLesson> => {
    const response = await api.get(`/training/lessons/${lessonId}`);
    return response.data.data;
  },

  // Get lesson by slug
  getLessonBySlug: async (moduleId: string, slug: string): Promise<TrainingLesson> => {
    const response = await api.get(`/training/modules/${moduleId}/lessons/slug/${slug}`);
    return response.data.data;
  },

  // Get module by slug
  getModuleBySlug: async (slug: string): Promise<any> => {
    const response = await api.get(`/training-modules/slug/${slug}`);
    return response.data.data;
  },

  // Get user's progress for a module
  getModuleProgress: async (moduleId: string, moduleName?: string): Promise<TrainingProgress> => {
    const params = moduleName ? { moduleName } : {};
    const response = await api.get(`/training/modules/${moduleId}/progress`, { params });
    return response.data.data;
  },

  // Mark lesson as complete
  completeLesson: async (
    lessonId: string,
    moduleId: string,
    quizScore?: number
  ): Promise<TrainingProgress> => {
    const response = await api.post(`/training/lessons/${lessonId}/complete`, {
      moduleId,
      quizScore,
    });
    return response.data.data;
  },

  // Submit final quiz
  submitFinalQuiz: async (
    moduleId: string,
    score: number,
    passed: boolean
  ): Promise<TrainingProgress> => {
    const response = await api.post(`/training/modules/${moduleId}/quiz`, {
      score,
      passed,
    });
    return response.data.data;
  },

  // Issue certificate
  issueCertificate: async (moduleId: string, certificateName: string): Promise<TrainingProgress> => {
    const response = await api.post(`/training/modules/${moduleId}/certificate`, {
      certificateName,
    });
    return response.data.data;
  },

  // Get all user's training progress
  getUserProgress: async (): Promise<TrainingProgress[]> => {
    const response = await api.get('/training/progress');
    return response.data.data;
  },

  // Reset module progress (allows retaking)
  resetModuleProgress: async (moduleId: string): Promise<void> => {
    await api.delete(`/training/modules/${moduleId}/progress`);
  },

  // Admin: Create lesson
  createLesson: async (lessonData: Partial<TrainingLesson>): Promise<TrainingLesson> => {
    const response = await api.post('/training/lessons', lessonData);
    return response.data.data;
  },

  // Admin: Update lesson
  updateLesson: async (lessonId: string, lessonData: Partial<TrainingLesson>): Promise<TrainingLesson> => {
    const response = await api.put(`/training/lessons/${lessonId}`, lessonData);
    return response.data.data;
  },

  // Admin: Delete lesson
  deleteLesson: async (lessonId: string): Promise<void> => {
    await api.delete(`/training/lessons/${lessonId}`);
  },
};

export default trainingService;
